import { Inject, Injectable } from '@angular/core';
import { SentryService } from '@rd-lib/sentry/sentry.service';
import { STRIPE_API_KEY } from '@rd/core/tokens/rd-lib.token';
import {
  ConfirmCardPaymentData,
  loadStripe,
  PaymentIntent,
  Stripe,
  StripeCardNumberElement,
  StripeElementClasses,
  StripeElements,
  StripeElementsOptions,
  StripeElementStyle,
  StripeError,
} from '@stripe/stripe-js';

export interface StripeConfirmPaymentResult {
  paymentIntent?: PaymentIntent;
  error?: StripeError;
}

export type ConfirmPaymentData =
  | {
      type: 'new_card';
      stripePaymentIntentClientSecret: string;
      saveCardForFutureUsage?: boolean;
      cardNumberElement: StripeCardNumberElement;
      cardName: string;
    }
  | {
      type: 'saved_card';
      stripePaymentIntentClientSecret: string;
      paymentMethodId: string;
    };

@Injectable({
  providedIn: 'root',
})
export class PaymentGatewayService {
  stripe: Stripe;
  stripeElements: StripeElements;
  isReady = false;
  isEnable = false;

  apiKey = '';

  // Customizing Stripe elements `config` - this is different from customizing element styles.
  // Think of Stripe as a completely different app from RD.
  // So loading fonts must be done here (see AnzPaymentGatewayService).
  stripeElementsConfig: StripeElementsOptions = {};

  // Customizing Stripe elements `style` - as Stripe is a different app,
  // Styles specific to Stripe elements are declared here.
  // They are used in `CardDetailForm` component.
  // This is where you'd customize Stripe elements (card number, card expiry
  // date, and CVC number.
  // Do not override styles via CSS.
  elementStyles: StripeElementStyle = {
    base: {
      color: '#32325D',
      fontWeight: '400',
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',

      '::placeholder': {
        color: '#CFD7DF',
      },
      ':-webkit-autofill': {
        color: '#e39f48',
      },
    },
    invalid: {
      color: '#E25950',

      '::placeholder': {
        color: '#FFCCA5',
      },
    },
  };

  elementClasses: StripeElementClasses = {
    focus: 'stripe-input-focused',
    empty: 'stripe-input-empty',
    invalid: 'stripe-input-invalid',
  };

  constructor(
    protected sentryService: SentryService,
    @Inject(STRIPE_API_KEY) protected stripeApiKey: string,
  ) {
    this.apiKey = this.stripeApiKey;
  }

  loadScript(): Promise<void> | void {
    if (this.isEnable && this.apiKey) {
      this.loadPaymentScript();
    }
  }

  loadPaymentScript(): Promise<void> {
    return loadStripe(this.apiKey).then((stripe: Stripe | null) => {
      if (stripe) {
        this.stripe = stripe;
        this.stripeElements = stripe.elements(this.stripeElementsConfig);
        this.isReady = true;
      } else {
        console.error('Failed to load Stripe');
      }
    });
  }

  handleConfirmCardPaymentResult({
    paymentResult,
  }: {
    paymentResult: StripeConfirmPaymentResult;
  }): Promise<StripeConfirmPaymentResult> {
    return new Promise((resolve, reject) => {
      if (paymentResult.error || !paymentResult.paymentIntent) {
        reject(paymentResult);
        return;
      }

      // possible stripe payment intent status
      // type Status =
      // | 'canceled'
      // | 'processing'
      // | 'requires_action'
      // | 'requires_capture'
      // | 'requires_confirmation'
      // | 'requires_payment_method'
      // | 'succeeded';

      // We only handle succeeded/canceled status for now
      if (paymentResult.paymentIntent.status === 'canceled') {
        reject(paymentResult);
        return;
      }

      if (paymentResult.paymentIntent.status === 'succeeded') {
        resolve(paymentResult);
        return;
      }

      // this is the NN happy case
      if (paymentResult.paymentIntent.status === 'requires_capture') {
        resolve(paymentResult);
        return;
      }

      const errorMessage = `Unexpected paymentIntent.status: ${paymentResult.paymentIntent.status}`;
      reject(new Error(errorMessage));
      this.sentryService.manualError(errorMessage);
    });
  }

  // this method should be called from the parent component
  confirmPayment(
    paymentData: ConfirmPaymentData,
  ): Promise<StripeConfirmPaymentResult> {
    const paymentRequestData = {} as ConfirmCardPaymentData;
    if (paymentData.type === 'new_card') {
      paymentRequestData.payment_method = {
        // this is the stripe magic, we only need to send the card number element
        // stripe can extract card expiry and card cvv from it
        card: paymentData.cardNumberElement,
        billing_details: {
          name: paymentData.cardName,
        },
      };
      if (paymentData.saveCardForFutureUsage) {
        paymentRequestData.setup_future_usage = 'on_session';
      }
    } else {
      paymentRequestData.payment_method = paymentData.paymentMethodId;
    }
    return this.stripe
      .confirmCardPayment(
        paymentData.stripePaymentIntentClientSecret,
        paymentRequestData,
      )
      .then((paymentResult) => {
        return this.handleConfirmCardPaymentResult({
          paymentResult,
        });
      });
  }
}
